import {
  Avatar,
  Box,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { type CSSObject, type Theme, styled, useTheme } from "@mui/material/styles";
import { useKeycloak } from "@react-keycloak/web";
import type React from "react";
import { useEffect, useMemo, useState } from "react";
import { jwtDecode } from "jwt-decode";
import ExportIcon from "../assets/export_Icon.svg";
import PDFIcon from "../assets/pdf_icon.svg";
import ReportIcon from "../assets/report_menu.svg";
import { fetchPartnerDetails } from "../common/makeAPICalls";
import type { PartnerTypes } from "../common/types";
import { useAuth } from "../component/AdminWrapper";
import { setPartnerDetailsStorage } from "../common/storage";
import { COMPANY_STORAGE_KEY } from "../Keycloak";
import { useTranslation } from "react-i18next";
import BasicButton from "./BasicButton";
import TransactionIcon from "../assets/transaction_icon.svg";
import transfertIcon from "../assets/transfert_icon.svg";
import logoutIcon from "../assets/logout.svg";
import { useNavigate } from "react-router-dom";
import dashboardIcon from "../assets/dashboard.svg";

interface SidebarProps {
  setOpenTransfertModal?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ setOpenTransfertModal }) => {
  const [open] = useState(true);
  const { t } = useTranslation();
  const [collapse, setCollapse] = useState(false);
  const { keycloak } = useKeycloak();
  const { tokens } = useAuth();
  const theme = useTheme();
  const [partnerDetails, setPartnerDetails] = useState<PartnerTypes | null>(
    null
  );
  const navigate = useNavigate();
  interface DecodedToken {
    name: string;
    [key: string]: unknown;
  }

  const fetchPartnerRecord = async () => {
    try {
      const details = await fetchPartnerDetails();
      setPartnerDetails(details);
      setPartnerDetailsStorage(details);
    } catch (error) {
      console.error("fetchPartnerDetails failed:", error);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetchPartnerRecord is a local async that only reads keycloak.token (via fetchWithAuth); adding it as a dep would re-run on every render
  useEffect(() => {
    if (tokens?.accessToken) {
      fetchPartnerRecord();
    }
  }, [tokens?.accessToken]);

  // Memoize JWT decode to avoid running on every render. Only re-runs when the
  // access token actually changes (after a refresh or login).
  const useInfo = useMemo<DecodedToken | null>(
    () =>
      tokens?.accessToken ? jwtDecode<DecodedToken>(tokens.accessToken) : null,
    [tokens?.accessToken]
  );
  
  const openedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden", // Modifié de "visible" à "hidden"
    overflowY: "auto", // Ajouté pour permettre le scroll vertical
    height: "calc(100vh - 90px)", // Ajouté pour définir une hauteur fixe
  });

  const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    overflowY: "auto", // Ajouté pour permettre le scroll vertical
    height: "calc(100vh - 90px)", // Ajouté pour définir une hauteur fixe
    [theme.breakpoints.up("sm")]: {
      width: `calc(${theme.spacing(8)} + 1px)`,
    },
  });

  const DrawerComp = styled(Drawer, {
    shouldForwardProp: (prop) => prop !== "open",
  })<{ open?: boolean }>(({ theme, open }) => ({
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    ...(open && {
      ...openedMixin(theme),
      "& .MuiDrawer-paper": openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      "& .MuiDrawer-paper": closedMixin(theme),
    }),
  }));

  const handleReportButtonClick = (link: string) => {
    window.open(`${link}`, "_blank", "noopener,noreferrer");
  };

  return (
    <DrawerComp
      anchor="left"
      variant="permanent"
      open={true}
      PaperProps={{
        elevation: 1,
        sx: {
          border: "1px solid rgba(255, 255, 255, 0.6)",
          backgroundColor: "transparent",
          padding: "10px",
          position: "static",
          overflow: "hidden",
          borderRadius: "10px",
          minWidth: "85px",
          height: "-webkit-fill-available !important",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          borderRadius: "5px",
          backgroundColor: "white",
          height: "100%",
        }}
      >
        <Box
          sx={{
            backgroundColor: "#FFF",
            borderRadius: "10px",
            padding: "20px",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            height: "100%", // Prend toute la hauteur
            color: "black",
            overflow: "hidden", // Cache le overflow sur ce conteneur
          }}
        >
          {/* Contenu scrollable */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              flex: 1, // Prend l'espace disponible
              overflowY: "auto", // Permet le scroll vertical
              paddingRight: "5px", // Espace pour la scrollbar
              // "&::-webkit-scrollbar": {
              //   width: "6px",
              // },
              // "&::-webkit-scrollbar-track": {
              //   background: "#f1f1f1",
              //   borderRadius: "3px",
              // },
              // "&::-webkit-scrollbar-thumb": {
              //   background: "#c1c1c1",
              //   borderRadius: "3px",
              //   "&:hover": {
              //     background: "#a1a1a1",
              //   },
              // },
              "&::-webkit-scrollbar": {
                display: "none",
              },
              scrollbarWidth: "none", // Firefox
              "-ms-overflow-style": "none",
            }}
          >
            {partnerDetails && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <Avatar
                  src="logo.svg"
                  alt={partnerDetails?.name || "B"}
                  sx={{ width: 70, height: 70 }}
                />
                <div style={{ textAlign: "center" }}>
                  <Typography fontWeight={600} fontSize={14}>
                    {partnerDetails?.name || partnerDetails?.firstName}
                  </Typography>
                  <Typography
                    color="#979797"
                    fontSize={14}
                    fontWeight={500}
                  >{`${partnerDetails?.city}, ${partnerDetails?.country}`}</Typography>
                </div>
              </div>
            )}
            <Divider sx={{ width: "100%", margin: "10px 0" }} />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "10px",
                width: "100%",
              }}
            >
              <BasicButton
                text={t("sidebar.accueil")}
                onClickEvent={() => navigate("/")}
                style={{
                  backgroundColor: theme.palette.secondary.main,
                  borderRadius: 3,
                  justifyContent: "flex-start",
                  alignItems: "center",
                  display: "inline-flex",
                  fontWeight: 400,
                  fontSize: 15,
                }}
                startIcon={<img src={dashboardIcon} alt="transaction" />}
              />
              <BasicButton
                text={t("button.transaction")}
                onClickEvent={() => navigate("/my-transactions")}
                style={{
                  backgroundColor: theme.palette.secondary.main,
                  borderRadius: 3,
                  justifyContent: "flex-start",
                  alignItems: "center",
                  display: "inline-flex",
                  fontWeight: 400,
                  fontSize: 15,
                }}
                startIcon={<img src={TransactionIcon} alt="transaction" />}
              />
              <BasicButton
                text={t("button.transfert")}
                onClickEvent={() =>
                  setOpenTransfertModal && setOpenTransfertModal()
                }
                style={{
                  backgroundColor: theme.palette.secondary.main,
                  borderRadius: 3,
                  justifyContent: "flex-start",
                  alignItems: "center",
                  display: "inline-flex",
                  fontWeight: 400,
                  fontSize: 15,
                }}
                startIcon={<img src={transfertIcon} alt="transfert" />}
              />
              <ListItemButton
                sx={{
                  width: "100%",
                  borderRadius: 1,
                  backgroundColor: (t) => t.palette.primary.main,
                  "&:hover": {
                    backgroundColor: (t) => t.palette.primary.dark,
                  },
                }}
                onClick={() => {
                  setCollapse(!collapse);
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    justifyContent: "center",
                  }}
                >
                  <img
                    src={ReportIcon}
                    alt="report_icon"
                    style={{
                      width: "16px",
                      height: "16px",
                      filter: "invert(1)",
                      transition: "filter 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      (e.target as HTMLImageElement).style.filter =
                        "invert(0.5)";
                    }}
                    onMouseLeave={(e) => {
                      (e.target as HTMLImageElement).style.filter = "invert(1)";
                    }}
                    loading="lazy"
                  />
                </ListItemIcon>
                <ListItemText
                  sx={{
                    pl: 2,
                    color: "white",
                    opacity: open ? 1 : 0,
                  }}
                  primary={t("button.report")}
                />
              </ListItemButton>
              <Collapse in={collapse} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemButton
                    sx={{
                      ml: open ? 2 : 1,
                      mr: 2,
                      borderRadius: 1,
                      borderColor: (t) => t.palette.primary.main,
                    }}
                    onClick={() =>
                      handleReportButtonClick(
                        "https://reports.bictorys.com/birt/frameset?__report=merchant/Resume_Marchand.rptdesign"
                      )
                    }
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 1 : "auto",
                        justifyContent: "center",
                      }}
                    >
                      <img src={ExportIcon} alt="export_icon" loading="lazy" />
                    </ListItemIcon>
                    <ListItemText
                      sx={{
                        opacity: open ? 1 : 0,
                        "& .MuiTypography-root": { fontSize: "14px" },
                      }}
                      primary={t("button.summary")}
                    />
                  </ListItemButton>
                  <ListItemButton
                    sx={{
                      ml: open ? 2 : 1,
                      mr: 2,
                      borderRadius: 1,
                      borderColor: (t) => t.palette.primary.main,
                    }}
                    onClick={() =>
                      handleReportButtonClick(
                        "https://reports.bictorys.com/birt/frameset?__report=merchant/Trans_Marchand.rptdesign"
                      )
                    }
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 1 : "auto",
                        justifyContent: "center",
                      }}
                    >
                      <img src={PDFIcon} alt="report_icon" loading="lazy" />
                    </ListItemIcon>
                    <ListItemText
                      sx={{
                        opacity: open ? 1 : 0,
                        "& .MuiTypography-root": { fontSize: "14px" },
                      }}
                      primary={t("button.transactionList")}
                    />
                  </ListItemButton>
                </List>
              </Collapse>
            </div>
          </Box>

          {/* Section footer fixe */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "10px",
              marginTop: "auto", // Pousse cette section vers le bas
              paddingTop: "20px", // Espacement du contenu scrollable
              borderTop: "1px solid #f0f0f0", // Ligne de séparation optionnelle
            }}
          >
            {useInfo?.name || useInfo?.given_name ? (
              <Typography variant="subtitle1">
                {useInfo?.name || (useInfo?.given_name as string)}
              </Typography>
            ) : null}
            <div
              style={{
                width: "100%",
                display: "flex",
                paddingRight: "10px",
                justifyContent: "left",
                alignItems: "center",
                gap: "6px",
                borderRadius: "22px",
                border: "1px solid #979797",
                background: "#FFF",
              }}
            >
              <Typography
                fontSize={14}
                sx={{
                  borderRadius: "22px",
                  background: "#979797",
                  color: "#FFF",
                  textAlign: "center",
                  padding: "4px 10px",
                  fontWeight: 700,
                }}
              >
                {t("sidebar.agentId")}
              </Typography>
              <Typography fontWeight={500} letterSpacing={0.6} fontSize={14}>
                {`${partnerDetails?.country}-${
                  typeof useInfo?.org_id === "string"
                    ? useInfo.org_id.split("-")[0]
                    : "N/A"
                }`}
              </Typography>
            </div>
            <BasicButton
              onClickEvent={() => {
                sessionStorage.removeItem(COMPANY_STORAGE_KEY);
                keycloak.logout({
                  redirectUri: window.location.origin,
                });
              }}
              text={t("sidebar.logout")}
              style={{
                backgroundColor: theme.palette.error.main,
                borderRadius: 3,
                justifyContent: "flex-start",
                alignItems: "center",
                display: "inline-flex",
                width: "100%",
                fontWeight: 400,
                fontSize: 15,
              }}
              startIcon={<img src={logoutIcon} alt="logout" loading="lazy" />}
            />
          </Box>
        </Box>
      </Box>
    </DrawerComp>
  );
};

export default Sidebar;
